import React, { FC, Fragment, useState } from 'react';
import { Box, Grid, FormControl, Button, InputAdornment, Alert, Link, Typography, Stack, Chip } from '@mui/material';
import { MaterialPriceList } from 'models/material';
import { useTranslation } from 'react-i18next';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import PriceListSelectorDialog, {
    PriceListOption,
} from '../../price-list-selector-dialog/price-list-selector-dialog.component';

type MaterialListPageActionsProps = {
    materialPriceList: MaterialPriceList;
    isUsingDefault: boolean;
    onSelectPriceList: (materialList: MaterialPriceList) => void;
    onUseDefault: () => void;
    onGeneralDiscountChange: (generalDiscount: string) => void;
    materialPriceLists: MaterialPriceList[];
    defaultMaterialPriceListId: string;
};

const MaterialListPageActions: FC<MaterialListPageActionsProps> = ({
    materialPriceList,
    isUsingDefault,
    onSelectPriceList,
    onUseDefault,
    onGeneralDiscountChange,
    materialPriceLists,
    defaultMaterialPriceListId,
}: MaterialListPageActionsProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'lab-settings']);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);

    const priceListOptions: PriceListOption[] = materialPriceLists.map((priceList) => ({
        _id: priceList._id || '',
        name: priceList.materialPriceListName,
        currency: priceList.currency,
        isStandardPriceList: priceList.isStandardPriceList,
    }));

    const handleSelect = (priceListId: string) => {
        const selectedPriceList = materialPriceLists.find((priceList) => priceList._id === priceListId);
        if (selectedPriceList) {
            onSelectPriceList(selectedPriceList);
        }
        setIsSelectorOpen(false);
    };

    if (!materialPriceLists.length) {
        return (
            <Alert severity="info">
                {translate('client-directory:NO_CREATED_MATERIAL_LISTS_INFO')}
                &nbsp;
                <Link href={`/lab-settings/material-settings`}>{translate('lab-settings:SHOW_SETTINGS')}</Link>
            </Alert>
        );
    }

    return (
        <Fragment>
            {!defaultMaterialPriceListId && (
                <Box mb={1}>
                    <Alert severity="info">
                        {translate('client-directory:NO_DEFAULT_MATERIAL_LIST')}
                        &nbsp;
                        <Link href={`/lab-settings/material-settings`}>{translate('lab-settings:SHOW_SETTINGS')}</Link>
                    </Alert>
                </Box>
            )}
            {isUsingDefault ? (
                <Box>
                    <Alert severity="info">{translate('client-directory:CLIENT_USES_DEFAULT_PRICE_LIST')}</Alert>
                    <Box mt={1}>
                        <Button variant="contained" onClick={() => setIsSelectorOpen(true)}>
                            {translate('client-directory:SELECT_PRICE_LIST')}
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Fragment>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ sm: 'center' }}
                        justifyContent="space-between"
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {translate('client-directory:PRICE_LIST')}:
                            </Typography>
                            <Chip
                                label={
                                    materialPriceList._id === defaultMaterialPriceListId
                                        ? `${materialPriceList.materialPriceListName} (${translate(
                                              'common:DEFAULT',
                                          ).toLowerCase()})`
                                        : materialPriceList.materialPriceListName
                                }
                                color="primary"
                                size="medium"
                            />
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Button onClick={() => setIsSelectorOpen(true)}>
                                {translate('client-directory:CHANGE_PRICE_LIST')}
                            </Button>
                            <Button color="secondary" onClick={onUseDefault}>
                                {translate('client-directory:USE_DEFAULT_PRICE_LIST')}
                            </Button>
                        </Stack>
                    </Stack>
                    <Grid container spacing={1} mt={0.5}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <NumberOnlyTextField
                                    id="general-discount"
                                    value={materialPriceList.generalDiscount || ''}
                                    label={translate('client-directory:GENERAL_DISCOUNT')}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        inputProps: { min: 0, max: 100 },
                                    }}
                                    onValueChange={onGeneralDiscountChange}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Fragment>
            )}
            {isSelectorOpen && (
                <PriceListSelectorDialog
                    open={isSelectorOpen}
                    title={translate('client-directory:SELECT_PRICE_LIST')}
                    options={priceListOptions}
                    selectedId={materialPriceList._id}
                    onSelect={handleSelect}
                    onClose={() => setIsSelectorOpen(false)}
                />
            )}
        </Fragment>
    );
};

export default MaterialListPageActions;
